import { useCallback, useState } from 'react';
import { Platform } from 'react-native';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { useAuthStore, useToastStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';

const NATIVE_IAP_PLATFORMS = ['ios', 'android', 'macos'];

export function canRestoreIapPurchases() {
  return NATIVE_IAP_PLATFORMS.includes(Platform.OS);
}

export default function useRestoreIapPurchases() {
  const { t } = useTranslation();
  const { iap } = useGabPurchase();
  const fetchUser = useAuthStore((state) => state.fetchUser);
  const fetchUserCredits = useAuthStore((state) => state.fetchUserCredits);
  const { addToast } = useToastStore((state) => state.actions);
  const [isRestoring, setIsRestoring] = useState(false);

  const restorePurchases = useCallback(async () => {
    if (isRestoring) return { success: false, error: 'Restore already in progress' };
    if (!iap?.restorePurchases) {
      addToast(t('settingsMobile.restorePurchasesFailed'), 'error');
      return { success: false, error: 'Store is not available' };
    }

    setIsRestoring(true);
    iap.clearError?.();
    try {
      const result = await iap.restorePurchases();
      if (!result?.success) {
        addToast(result?.error || t('settingsMobile.restorePurchasesFailed'), 'error');
        return result || { success: false };
      }

      await Promise.all([fetchUser?.(), fetchUserCredits?.()].filter(Boolean));
      const isNowPlus = !!useAuthStore.getState().user?.flags?.isPlus;
      if (result?.message === 'No purchases to restore' && !isNowPlus) {
        addToast(t('settingsMobile.restorePurchasesNone'), 'info');
      } else {
        addToast(t('settingsMobile.restorePurchasesSuccess'), 'success');
      }
      return result;
    } catch (error) {
      addToast(error?.message || t('settingsMobile.restorePurchasesFailed'), 'error');
      return { success: false, error: error?.message };
    } finally {
      setIsRestoring(false);
    }
  }, [addToast, fetchUser, fetchUserCredits, iap, isRestoring, t]);

  return {
    canRestore: canRestoreIapPurchases(),
    isRestoring,
    restorePurchases,
  };
}
